// Prochaine action d'une affaire (conception du suivi des affaires, §§ 4 et 5) : types, échéances en un clic,
// relance proposée après un message, effet d'une réponse. Logique pure, testée ; les jours sont des chaînes
// AAAA-MM-JJ du jour local, calculées en UTC pour ne pas dépendre du fuseau ni du changement d'heure.
import type { Identifier } from "ra-core";

export const NEXT_ACTION_TYPES = [
  "follow_up",
  "call",
  "send_documentation",
  "propose_meeting",
  "present",
  "site_visit",
  "check_cctp",
  "contact_contractor",
  "linkedin_dm",
  "other",
] as const;
export type NextActionType = (typeof NEXT_ACTION_TYPES)[number];

export const DUE_CHOICES = ["tomorrow", "in3", "monday", "in2weeks"] as const;
export type DueChoice = (typeof DUE_CHOICES)[number];

// Relance proposée après un message sans réponse (valeur par défaut notée dans la page client du 25/10/2026).
export const FOLLOW_UP_BUSINESS_DAYS = 5;

// Délai laissé à la personne pour accepter une demande de connexion avant le premier message (décision du
// 26/10/2026) ; l'outil ne voit pas l'acceptation, l'utilisateur la constate à l'échéance.
export const LINKEDIN_DM_BUSINESS_DAYS = 3;

const pad = (n: number) => String(n).padStart(2, "0");

/** Jour local, au format des colonnes `date` de la base. */
export const today = (now: Date = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

const parse = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const format = (date: Date) => date.toISOString().slice(0, 10);

export const addDays = (day: string, n: number) => {
  const date = parse(day);
  date.setUTCDate(date.getUTCDate() + n);
  return format(date);
};

// Lundi à vendredi, sans jours fériés (estimation acceptée dans la conception).
export const addBusinessDays = (day: string, n: number) => {
  const date = parse(day);
  let left = n;
  while (left > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) left -= 1;
  }
  return format(date);
};

export const dueDate = (choice: DueChoice, day: string) => {
  switch (choice) {
    case "tomorrow":
      return addDays(day, 1);
    case "in3":
      return addBusinessDays(day, 3);
    case "monday": {
      const weekday = parse(day).getUTCDay();
      return addDays(day, (8 - weekday) % 7 || 7);
    }
    case "in2weeks":
      return addDays(day, 14);
  }
};

export type ProposedAction = {
  type: NextActionType;
  date: string;
  contactId: number | null;
  contactName: string | null;
};

/** Relance proposée après un message parti ; rien si une action est déjà prévue au plus tard ce jour-là. */
export const proposeFollowUp = (
  deal: { next_action_date?: string | null },
  contact: { id: number; name: string } | null,
  day: string,
): ProposedAction | null => {
  const date = addBusinessDays(day, FOLLOW_UP_BUSINESS_DAYS);
  if (deal.next_action_date && deal.next_action_date <= date) return null;
  return {
    type: "follow_up",
    date,
    contactId: contact?.id ?? null,
    contactName: contact?.name ?? null,
  };
};

/** Après une demande de connexion : le premier message, à cette personne, avec son texte, en prochaine action. */
export const linkedInReminder = (
  contact: { id: number; name: string },
  message: string,
  day: string,
) => ({
  next_action_type: "linkedin_dm" as const,
  next_action_date: addBusinessDays(day, LINKEDIN_DM_BUSINESS_DAYS),
  next_action: contact.name,
  next_action_contact_id: contact.id,
  next_action_message: message.trim() || null,
});

/** Une réponse rend inutile la relance vers cette personne, ou la relance de la société sans personne. */
export const clearsOnReply = (
  deal: {
    next_action_type?: string | null;
    next_action_contact_id?: Identifier | null;
  },
  contactId: Identifier | null,
) =>
  deal.next_action_type === "follow_up" &&
  (deal.next_action_contact_id == null ||
    deal.next_action_contact_id === contactId);

/** Type d'échange préremplie quand on marque une action « Faite ». */
export const activityKindFor = (
  type: NextActionType | null | undefined,
): "email" | "linkedin" | "call" | "meeting" | "site_visit" | "documentation" => {
  switch (type) {
    case "linkedin_dm":
      return "linkedin";
    case "call":
      return "call";
    case "site_visit":
      return "site_visit";
    case "send_documentation":
      return "documentation";
    case "present":
    case "propose_meeting":
      return "meeting";
    default:
      return "email";
  }
};

/** Action accomplie ou devenue sans objet : tout est retiré. */
export const CLEARED_ACTION = {
  next_action_type: null,
  next_action_date: null,
  next_action: null,
  next_action_contact_id: null,
} as const;
